import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button } from '@material-ui/core';
import { Alert, TextField, InputLabel, FormHelperText, InputAdornment } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import notes from 'assets/onboarding/notes.svg';
import { ModalTitle } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import { FilesUpload } from 'containers/FilesUpload/FilesUpload';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import Img from 'retired/Img';

import { BrandFormData, BrandField } from './types';
import { PrevNextButtons } from './PrevNextButtons';

const brandPages: BrandField[] = [
  {
    name: 'Facebook',
    placeholder: 'Facebook Page URL',
    ref: 'facebookPage',
    pattern: {
      value:
        /^(?:https?:\/\/)?(?:www\.)?(?:facebook|fb|m\.facebook)\.(?:com|me)\/(?:(?:\w)*#!\/)?(?:pages\/)?(?:[\w?=&-]*\/)*([\w?=&\-.]+)(?:\/)?$/i,
      message: 'Invalid Facebook address, must look like "https://www.facebook.com/joindeednow"',
    },
  },
  {
    name: '𝕏 (formerly Twitter)',
    placeholder: '𝕏 (formerly Twitter) handle',
    ref: 'xSocial',
    pattern: {
      value: /^[a-z\d_]{1,15}$/i,
      message: 'Invalid 𝕏 handle, must look like "join_deed"',
    },
    startAdornment: 'x.com/',
  },
  {
    name: 'Instagram',
    placeholder: 'Instagram handle',
    ref: 'instagram',
    pattern: {
      value: /^[a-z\d._]{1,255}$/i,
      message: 'Invalid Instagram handle, must look like "join.deed"',
    },
    startAdornment: 'instagram.com/',
  },
];

export const Brand = ({ send, isTransitionAllowed, state }: StepComponentProps): JSX.Element => {
  // TODO @EV: add error handling logic for file uploads section
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFailed, setIsFailed] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);

  const isSubmitting = Boolean(
    typeof state.value === 'object' && 'brand' in state.value
      ? state.value.brand === 'submitting'
      : false
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      mainPicture: organization?.mainPicture,
      facebookPage: organization?.facebookPage,
      xSocial: organization?.xSocial,
      instagram: organization?.instagram,
    },
  });

  const onSubmit = (data: BrandFormData) => {
    send({ type: 'SUBMIT', data });
  };

  return (
    <ModalStep
      showProgressBar
      actions={
        <div style={{ display: 'flex' }}>
          {user && user.isAdmin && (
            <PrevNextButtons
              isPrevTransitionAllowed={isTransitionAllowed}
              isNextTransitionAllowed={isTransitionAllowed}
              sendPrev={send}
              sendNext={send}
            />
          )}
          <Button
            type="button"
            variant="contained"
            disabled={isUploadingLogo || isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
        </div>
      }
      header={<Img src={notes} />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box mb={4}>
        <ModalTitle>Please provide more information</ModalTitle>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 16,

            height: '100%',
          }}
        >
          <div>
            <InputLabel style={{ marginBottom: 8 }}>
              {organization?.mainPicture ? (
                <>
                  You currently have a logo.
                  <br />
                  If you’d like to replace it, please upload the new one here.
                </>
              ) : (
                'Upload your logo here.'
              )}
            </InputLabel>
            <Controller
              control={control}
              name="mainPicture"
              render={({ field: { onChange } }) => (
                <FilesUpload
                  acceptedTypes={['image/png']}
                  s3Acl="public-read"
                  s3Bucket="primary"
                  onChange={(files, uploadInProgress) => {
                    const publicFileUrl = files?.[0]?.publicUrl;
                    if (uploadInProgress) {
                      setIsUploadingLogo(true);
                    }
                    if (!uploadInProgress) {
                      onChange(publicFileUrl);
                      setIsUploadingLogo(false);
                    }
                  }}
                  multiple={false}
                  imagePreview
                  previousFileUrl={organization?.mainPicture}
                />
              )}
            />
            <FormHelperText error={Boolean(errors.mainPicture?.message) || undefined}>
              Upload an image of your organization&lsquo;s logo. Make sure the image is transparent
              and in PNG format.
            </FormHelperText>
            {isFailed && (
              <Box mt={2}>
                <Alert severity="error">Failed to submit the file, please try again</Alert>
              </Box>
            )}
          </div>
          {brandPages.map((page) => (
            <Controller
              control={control}
              key={page.ref}
              name={page.ref}
              defaultValue=""
              rules={{
                pattern: page.pattern,
              }}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    label={page.name}
                    helperText={page.placeholder}
                    fullWidth
                    variant="standard"
                    error={Boolean(fieldState.error)}
                    InputProps={
                      page.startAdornment
                        ? {
                            startAdornment: (
                              <InputAdornment position="start">
                                {page.startAdornment}
                              </InputAdornment>
                            ),
                          }
                        : {}
                    }
                  />
                  {fieldState.error ? (
                    <FormHelperText error={Boolean(fieldState.error)} style={{ marginTop: -8 }}>
                      {fieldState.error.message}
                    </FormHelperText>
                  ) : null}
                </>
              )}
            />
          ))}
        </form>
        {state.context.error && (
          <Alert severity="error" style={{ marginTop: '20px' }}>
            Submission failed, please try again. If the error persists, please contact support.{' '}
          </Alert>
        )}
      </Box>
    </ModalStep>
  );
};
